import React, { useEffect, useRef, useState } from 'react';


const LABELS = {
  ai: 'AI 判断诊断',
  cache_hit: '缓存命中',
  decision: '解析结果',
  duration_ms: '耗时',
  error: '错误',
  expected_value: '期望值',
  fallback_reason: '回退原因',
  field_id: '字段 ID',
  field_name: '字段名称',
  final_prompt: '最终提示词',
  input_fields: '输入字段与值',
  include_images: '包含工单图片',
  image_count: '图片数量',
  images: '图片附件与摘要',
  issue_data_source: '工单数据来源',
  logic: '判断逻辑',
  matched: '是否匹配',
  matched_handles: '匹配成功的 Handle',
  unmatched_handles: '未匹配的 Handle（含冲突或无效项）',
  matched_handle_count: '匹配成功的 Handle 数',
  matched_customer_count: '匹配客户数',
  message: '结果消息',
  normalized_response: '标准化响应',
  normalized_value: '标准化值',
  operator: '操作符',
  prompt: '配置提示词',
  provider: '模型提供商',
  raw_actual_value: '原始字段值',
  raw_response: '模型原始响应',
  reason: '判断原因',
  reason_code: '原因代码',
  safe_config: '安全动作配置',
  status: '状态',
  type: '类型'
};

const displayValue = (value) => {
  if (value === true) return '是';
  if (value === false) return '否';
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const isLongValue = (value) => (
  typeof value === 'object'
  || displayValue(value).length > 160
  || displayValue(value).includes('\n')
);

const TraceValue = ({ label, value }) => {
  const rendered = displayValue(value);
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef(null);

  useEffect(() => () => {
    window.clearTimeout(resetTimerRef.current);
  }, []);

  const handleCopy = async () => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = window.setTimeout(
        () => setCopied(false),
        1500
      );
    } catch {
      setCopied(false);
    }
  };

  if (isLongValue(value)) {
    return (
      <div className="execution-trace-inspector-field">
        <details>
          <summary>{label}</summary>
          <pre>{rendered}</pre>
        </details>
        <button
          type="button"
          className={`execution-trace-copy${copied ? ' copied' : ''}`}
          onClick={handleCopy}
        >
          {copied && (
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="m5 12 4 4L19 6" />
            </svg>
          )}
          {copied ? '已复制' : '复制'}
        </button>
      </div>
    );
  }

  return (
    <div className="execution-trace-inspector-field">
      <span>{label}</span>
      <strong>{rendered}</strong>
    </div>
  );
};

const renderEntries = (value, prefix = '') => {
  if (!value || typeof value !== 'object') return null;
  return Object.entries(value)
    .filter(([key]) => ![
      'conditions',
      'condition_groups',
      'rules',
      'actions',
      'direct_actions'
    ].includes(key))
    .map(([key, item]) => {
      const label = LABELS[key] || prefix
        ? `${prefix}${LABELS[key] || key}`
        : key;
      return (
        <TraceValue
          key={`${prefix}-${key}`}
          label={label}
          value={item}
        />
      );
    });
};

const CloseButton = ({ onClose }) => (
  <button
    type="button"
    className="execution-trace-inspector-close"
    aria-label="关闭节点详情"
    title="关闭节点详情"
    onClick={onClose}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  </button>
);

const ExecutionTraceInspector = ({ node, onClose }) => {
  if (!node) {
    return (
      <aside className="execution-trace-inspector">
        <div className="execution-trace-inspector-heading">
          <h3>节点详情</h3>
          {onClose && <CloseButton onClose={onClose} />}
        </div>
        <p className="execution-trace-empty-copy">
          选择流程节点查看保存时的判断信息。
        </p>
      </aside>
    );
  }

  return (
    <aside className="execution-trace-inspector">
      <div className="execution-trace-inspector-heading">
        <h3>节点详情</h3>
        <div className="execution-trace-inspector-actions">
          <span className={`badge badge-${node.status === 'error' || node.status === 'failed' ? 'danger' : 'default'}`}>
            {node.status || 'unknown'}
          </span>
          {onClose && <CloseButton onClose={onClose} />}
        </div>
      </div>
      <div className="execution-trace-inspector-fields">
        {renderEntries(node)}
      </div>
      {node.ai && (
        <section className="execution-trace-ai-section">
          <h4>AI 判断诊断</h4>
          {renderEntries(node.ai)}
        </section>
      )}
      {node.truncated && (
        <p className="execution-trace-truncated">
          此节点包含因日志大小限制而截断的内容。
        </p>
      )}
    </aside>
  );
};


export default ExecutionTraceInspector;
